import {
  ExistingApplicationPage,
  LoadingPage,
} from '@resurs-internal/deposits-app-components';
import { CountryContext } from 'contexts/CountryContext';
import { isEmpty } from 'helpers/isEmpty';
import { useProducts } from 'hooks/useProducts';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router';

import {
  getConfigurationRuleId,
  hasFixedTermView,
  isAuthenticated,
  sendGTMApplicationStart,
} from '@/helpers';
import { useApplication, useCampaignCode } from '@/hooks';
import {
  fetchApplication,
  initApplication,
  resetApplication,
  saveApplication,
} from '@/services';
import {
  ApplicationStatus,
  type DepositProduct,
  type InitApplicationResponse,
  type InitiateConflictResponse,
  type NotOldEnoughResponse,
  type ProductBoundaryExceededResponse,
  ProductBoundaryType,
} from '@/types/consumer-deposit-application-service';

const isNewApplicationResponse = (
  application:
    | InitApplicationResponse
    | InitiateConflictResponse
    | NotOldEnoughResponse
    | ProductBoundaryExceededResponse,
): application is InitApplicationResponse => {
  return (
    (application as InitApplicationResponse)?.termsConditionsAccepted !==
    undefined
  );
};

export const NewApplicationView = () => {
  const navigate = useNavigate();
  const { country } = useContext(CountryContext);
  const { product } = useOutletContext<{ product: DepositProduct }>();
  const { products } = useProducts(country);
  const { appendCampaignCode, campaignCode } = useCampaignCode();
  const [searchParams] = useSearchParams();
  const selectedRuleId = searchParams.get('configurationRuleId');
  const [existingProduct, setExistingProduct] = useState<
    DepositProduct | undefined
  >(undefined);
  const [existingCampaignCode, setExistingCampaignCode] = useState<
    string | null
  >(null);
  const [existingTerm, setExistingTerm] = useState<string | undefined>(
    undefined,
  );
  const configurationRuleId = product
    ? getConfigurationRuleId(product, selectedRuleId)
    : undefined;
  const newTerm = product?.configurationRules?.find(
    (rule) => rule.id === configurationRuleId,
  )?.term;

  const sendGTMStart = useCallback(
    (applicationId: string, productVariant: string) => {
      sendGTMApplicationStart({
        applicationId,
        country,
        productVariant,
      });
    },
    [country],
  );

  const [hasExistingApplication, setHasExistingApplication] =
    useState<boolean>(false);
  const [applicationId, setApplicationId] = useState<string>('');
  const { application } = useApplication(applicationId);
  const hasInitialized = useRef(false);

  const handleReset = () => {
    if (
      !product ||
      !configurationRuleId ||
      !applicationId ||
      isEmpty(applicationId)
    ) {
      navigate('/error');

      return;
    }

    resetApplication(
      applicationId,
      product.code,
      existingCampaignCode ?? undefined,
      configurationRuleId,
    )
      .then((result) => {
        if (result && isNewApplicationResponse(result)) {
          sendGTMStart(result.applicationId, product.name);
          navigate(result.applicationId, { replace: true });
        } else {
          navigate('/error');
        }
      })
      .catch((error) => {
        console.error('Reset application failed', error);
        navigate('/error');
      });
  };

  const handleContinue = () => {
    if (!existingProduct) {
      navigate('/error');

      return;
    }
    application.setProductCode(existingProduct.code);
    navigate(applicationId, { replace: true });
  };

  useEffect(() => {
    if (hasInitialized.current) return;

    if (!product) {
      navigate('/error');

      return;
    }

    // A product with several rules needs a selected one, e.g. when the
    // application is opened without going through the fixed term view.
    if (!configurationRuleId && hasFixedTermView(product)) {
      hasInitialized.current = true;
      navigate(
        appendCampaignCode(
          `/choose-fixed-term/${encodeURIComponent(product.code)}`,
        ),
        {
          replace: true,
        },
      );

      return;
    }

    // An application is never started without a rule. The backend returns at
    // least one configuration rule per product. A product with several rules
    // that is not a fixed product has no rule to select, so it ends on the
    // error view.
    if (!configurationRuleId) {
      hasInitialized.current = true;
      navigate('/error');

      return;
    }

    if (isAuthenticated() && products.length > 0) {
      hasInitialized.current = true;
      initApplication(product.code, campaignCode, configurationRuleId)
        .then((result) => {
          if (result && Object.keys(result).includes('applicationId')) {
            setApplicationId((result as InitApplicationResponse).applicationId);
            if (isNewApplicationResponse(result)) {
              sendGTMStart(result.applicationId, product.name);
              const defaultApplication = {
                depositGuaranteeAccepted: false,
                productCode: product.code,
                status: ApplicationStatus.PROCESSING,
                termsConditionsAccepted: result.termsConditionsAccepted,
                ...(campaignCode && { campaignCode }),
              };

              saveApplication(result.applicationId, defaultApplication)
                .then(() => {
                  navigate(result.applicationId);
                })
                .catch(() => {
                  navigate('/error');
                });
            } else {
              const resultAsConflict = result as InitiateConflictResponse;

              if (!resultAsConflict?.resumable) {
                navigate(`${resultAsConflict.applicationId}/pending`, {
                  state: { code: '409' },
                });

                return;
              } else {
                fetchApplication(resultAsConflict.applicationId)
                  .then((existingApplication) => {
                    const productCode =
                      existingApplication.application.productCode;

                    if (products.length > 0) {
                      const foundProduct = products.find(
                        (p) => p.code === productCode,
                      );

                      setExistingCampaignCode(
                        existingApplication.application?.campaignCode ?? null,
                      );
                      setExistingTerm(existingApplication.application?.term);
                      setHasExistingApplication(true);
                      setExistingProduct(foundProduct);
                    }
                  })
                  .catch(() => {
                    navigate('/error');
                  });
              }
            }
          } else {
            if ((result as NotOldEnoughResponse)?.code === '451') {
              navigate('/underage');
            } else if (
              (result as ProductBoundaryExceededResponse)?.boundary?.type ===
              ProductBoundaryType.MAX_ACCOUNTS
            ) {
              navigate(appendCampaignCode('max-accounts/' + product.code));
            } else {
              navigate('/error');
            }
          }
        })
        .catch(() => {
          navigate('/error');
        });
    }
  }, [
    country,
    navigate,
    product,
    products,
    sendGTMStart,
    appendCampaignCode,
    campaignCode,
    configurationRuleId,
  ]);

  if (hasExistingApplication && applicationId && product) {
    return (
      <ExistingApplicationPage
        continueWithProduct={handleContinue}
        newProduct={product}
        newTerm={newTerm}
        previousProduct={existingProduct ?? product}
        previousTerm={existingTerm}
        resetWithNewProduct={handleReset}
      />
    );
  }

  return <LoadingPage country={country} />;
};
